import React, { useEffect, useState, useRef } from "react";
import { Formik, Form, Field, ErrorMessage } from "formik";
import * as Yup from "yup";
import { useDispatch } from "react-redux";
import { useLocation, useNavigate } from "react-router-dom";
import { setActivePage, setShowPreloader } from "../store/activePage";
import { ErrorToast, SuccessToast } from "../components/toastify";
import { AxiosGetMethod, AxiosPostMethod } from "../helpers/customAxios";
import CustomModal from "../components/modal";
import HandleCreateEvent from "../hooks/handleCreateEvent";
import HandleLogout from "../hooks/handleLogout";
import DesktopFileUpload from "../components/fileupload";
import { FaCheckCircle } from "react-icons/fa";
import convertoformdata from "../helpers/convertToFormdata";
import GetEventTypeList from "../hooks/getEventTypeList";
import PaymentComponent from "../components/payment-gateway";
import handleUpdateEventPayment from "../hooks/handleUpdatePayment";
import DateTimeSegmentInput from "../components/date-time-segment-input";
import CitySearchSelect from "../components/city-search-select";

const OLD_RECORD_DRAFT_STORAGE_KEY = "operator-old-record-draft";

// ─── Shared class helpers ─────────────────────────────────────────────────────
const inputCls =
  "w-full rounded-xl border border-slate-200 bg-slate-50 px-3.5 py-2.5 text-sm font-medium text-slate-900 placeholder-slate-400 transition focus:border-teal-500 focus:outline-none focus:ring-2 focus:ring-teal-500/20";

const selectCls =
  "w-full rounded-xl border border-slate-200 bg-slate-50 px-3.5 py-2.5 text-sm font-medium text-slate-900 transition appearance-none focus:border-teal-500 focus:outline-none focus:ring-2 focus:ring-teal-500/20";

const textareaCls =
  "w-full resize-none rounded-xl border border-slate-200 bg-slate-50 px-3.5 py-2.5 text-sm font-medium text-slate-900 placeholder-slate-400 transition focus:border-teal-500 focus:outline-none focus:ring-2 focus:ring-teal-500/20";

// ─── Field Box ────────────────────────────────────────────────────────────────
function FieldBox({ label, required, error, children, span }) {
  return (
    <div className={span === 2 ? "md:col-span-2" : ""}>
      <label className="block text-xs font-bold text-gray-500 uppercase tracking-widest mb-1.5">
        {label} {required && <span className="text-red-500">*</span>}
      </label>
      {children}
      {error && (
        <p className="text-xs font-semibold text-red-500 mt-1">{error}</p>
      )}
    </div>
  );
}

// ─── Section Header ───────────────────────────────────────────────────────────
function SectionHeader({ icon, title, sub }) {
  return (
    <div className="flex items-center gap-3 mb-4 pb-3 border-b border-gray-100">
      <span className="text-xl">{icon}</span>
      <div>
        <p className="text-sm font-black text-gray-900">{title}</p>
        {sub && <p className="text-xs text-gray-500">{sub}</p>}
      </div>
    </div>
  );
}

// ─── Customer Status Badge ────────────────────────────────────────────────────
function CustomerBadge({ validCus }) {
  if (validCus === null) return null;
  return (
    <div
      className={`flex items-center gap-1.5 text-xs font-semibold px-3 py-1.5 rounded-lg mt-2 ${
        validCus
          ? "bg-teal-50 text-teal-700 border border-teal-200"
          : "bg-red-50 text-red-600 border border-red-200"
      }`}
    >
      <span>{validCus ? "✓" : "✗"}</span>
      {validCus ? "Customer verified" : "Customer not found"}
    </div>
  );
}

function CustomerInfoCard({ info }) {
  if (!info) return null;
  const rows = [
    { label: "Name", value: `${info.first_name} ${info.last_name}` },
    { label: "Occupation", value: info.occupation },
    { label: "Mobile", value: info.mobile },
    { label: "Email", value: info.email },
    { label: "Address", value: info.address },
    { label: "City", value: info.city },
    { label: "State", value: info.state },
  ];
  return (
    <div className="mt-1 rounded-xl border border-teal-200 bg-teal-50/60 p-4 md:col-span-2">
      <p className="mb-3 flex items-center gap-1.5 text-xs font-bold uppercase tracking-widest text-teal-700">
        <span>✓</span> Customer Details (Read-only)
      </p>
      <div className="grid grid-cols-2 gap-x-6 gap-y-3 sm:grid-cols-3">
        {rows.map(({ label, value }) => (
          <div key={label}>
            <p className="mb-0.5 text-[10px] font-bold uppercase tracking-wider text-slate-500">
              {label}
            </p>
            <input
              type="text"
              disabled
              readOnly
              value={value || "-"}
              className="w-full cursor-not-allowed rounded-lg border border-slate-200 bg-white px-2.5 py-1.5 text-xs font-medium text-slate-700 opacity-80"
            />
          </div>
        ))}
      </div>
    </div>
  );
}

// ─── Main Component ───────────────────────────────────────────────────────────
const UploadOldRecord = () => {
  const [eventTypes, setEventTypes] = useState([]);
  const [validCus, setValidCus] = useState(null);
  const [customerInfo, setCustomerInfo] = useState(null);
  const [showCusWrngModal, setShowCusWrngModal] = useState(false);
  const [pendingCustomerMobile, setPendingCustomerMobile] = useState("");
  const [pendingRegistrationDraft, setPendingRegistrationDraft] = useState(null);
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const location = useLocation();
  const formRef = useRef(null);
  const formikRef = useRef(null);
  const queryParams = new URLSearchParams(location.search);
  const customerMobileFromQuery = queryParams.get("customerMobile") || "";
  const getEmptyInitialValues = () => ({
    event_type: "",
    custom_event_type: "",
    event_owner_name: "",
    eventor_1: "",
    eventor_2: "",
    event_date: "",
    event_address: "",
    event_city: "",
    event_state: "",
    event_pincode: "",
    customer_mobile: "",
    customer_id: "",
    invitation: "",
  });

  // ─── Enter Key Jump Logic ─────────────────────────────────────────────────
  const handleKeyDown = (e) => {
    if (e.key === "Enter") {
      const target = e.target;
      if (target.type === "submit") return;
      e.preventDefault();
      const nodes = Array.from(
        formRef.current.querySelectorAll(
          'input:not([disabled]), select:not([disabled]), textarea:not([disabled]), button[type="submit"]'
        )
      );
      const currentIndex = nodes.indexOf(target);
      if (currentIndex > -1 && currentIndex < nodes.length - 1) {
        nodes[currentIndex + 1].focus();
      }
    }
  };

  const today = new Date();
  const formattedToday = today.toISOString();

  const getInitialValues = () => {
    const savedDraft = customerMobileFromQuery
      ? JSON.parse(
          sessionStorage.getItem(OLD_RECORD_DRAFT_STORAGE_KEY) || "{}",
        )
      : {};

    return {
      event_type: savedDraft.event_type || "",
      custom_event_type: savedDraft.custom_event_type || "",
      event_owner_name: savedDraft.event_owner_name || "",
      eventor_1: savedDraft.eventor_1 || "",
      eventor_2: savedDraft.eventor_2 || "",
      event_date: savedDraft.event_date || "",
      event_address: savedDraft.event_address || "",
      event_city: savedDraft.event_city || "",
      event_state: savedDraft.event_state || "",
      event_pincode: savedDraft.event_pincode || "",
      customer_mobile: customerMobileFromQuery || "",
      customer_id: "",
      invitation: "",
    };
  };

  const [initialValues, setInitialValues] = useState(getInitialValues);

  const validationSchema = Yup.object({
    event_type: Yup.string().required("Event type is required"),
    custom_event_type: Yup.string().when("event_type", {
      is: "Custom",
      then: (schema) => schema.required("Custom event type is required"),
      otherwise: (schema) => schema.notRequired(),
    }),
    event_owner_name: Yup.string().required("Owner or family name is required"),
    eventor_1: Yup.string().trim(),
    eventor_2: Yup.string().trim(),
    event_date: Yup.date()
      .max(formattedToday, "Event date must be in the past")
      .required("Event date is required"),
    event_address: Yup.string().required("Event address is required"),
    event_city: Yup.string().required("City is required"),
    event_state: Yup.string().required("State is required"),
    event_pincode: Yup.number()
      .typeError("Pincode must be a number")
      .required("Pincode is required"),
    customer_mobile: Yup.string().matches(
      /^[0-9]{10}$/,
      "Must be exactly 10 digits"
    ),
  }).shape({
    invitation: Yup.mixed()
      .nullable()
      .test("fileType", "Only image files are allowed", (value) =>
        !value || ["image/jpeg", "image/png"].includes(value.type)
      )
      .test("fileSize", "File must be under 1MB", (value) =>
        !value || value.size <= 1048576
      ),
  });

  const HandleSuccess = (razval, event_id) => {
    SuccessToast("Payment successful");
    handleUpdateEventPayment(
      { ...razval, event_id, cost: process.env.REACT_APP_EVENT_PRICING },
      dispatch
    );
  };

  const HandleFailure = () =>
    ErrorToast("Payment failed. Please try again later.");

  const continuetoPayment = async (event_id) => {
    dispatch(setShowPreloader(true));
    const orderDetails = await AxiosPostMethod("org/event/order-create", {
      amount: process.env.REACT_APP_EVENT_PRICING,
    });
    dispatch(setShowPreloader(false));
    if (orderDetails.status === 200) {
      PaymentComponent(
        orderDetails.data.data,
        (razval) => HandleSuccess(razval, event_id),
        HandleFailure
      );
    } else if (orderDetails.status === 401) HandleLogout();
    else ErrorToast("Error creating payment");
  };

  const createEventHandler = async (formvalue, resetForm) => {
    dispatch(setShowPreloader(true));
    HandleCreateEvent(
      convertoformdata(formvalue),
      dispatch,
      resetForm,
      continuetoPayment
    );
  };

  const handleSubmit = async (values, { resetForm }) => {
    if (values.event_type === "Custom")
      values.event_type = values.custom_event_type;
    values.event_date =
      values.event_date.split("T")[0] +
      " " +
      values.event_date.split("T")[1] +
      ":00";
    values.old_entry = 1;
    values.eventDetailsEntered = true;
    values.reminder_sms = 0;
    values.reminder_voice = 0;
    values.moi_confirmation_sms = 0;
    values.accept_online_payments = 0;
    values.featuresDetailsEntered = true;
    values.cost = process.env.REACT_APP_EVENT_PRICING;

    if (!validCus) {
      setPendingCustomerMobile(values.customer_mobile || "");
      setPendingRegistrationDraft({ ...values });
      setShowCusWrngModal(true);
      return null;
    }
    const clearedInitialValues = getEmptyInitialValues();
    setInitialValues(clearedInitialValues);
    resetForm({ values: clearedInitialValues });
    sessionStorage.removeItem(OLD_RECORD_DRAFT_STORAGE_KEY);
    navigate(location.pathname, { replace: true });
    setPendingRegistrationDraft(null);
    setPendingCustomerMobile("");
    setValidCus(null);
    setCustomerInfo(null);
    createEventHandler(values, resetForm);
  };

  useEffect(() => {
    dispatch(
      setActivePage({
        headerName: "Event Details Form",
        activeMenu: "oldRecordUpload",
      })
    );
  }, []);

  useEffect(() => {
    (async () => {
      const list = await GetEventTypeList();
      if (list.length > 0) setEventTypes(list.map((i) => i.event_name));
    })();
  }, []);

  useEffect(() => {
    if (!customerMobileFromQuery) return;

    const timer = setTimeout(() => {
      checkCustomerMobile(customerMobileFromQuery, (field, value) => {
        formikRef.current?.setFieldValue(field, value);
      });
    }, 0);

    return () => clearTimeout(timer);
  }, [customerMobileFromQuery]);

  const checkCustomerMobile = async (value, setFieldValue) => {
    dispatch(setShowPreloader(true));
    const res = await AxiosGetMethod(
      `org/get-customer-info?phone_number=${value}`
    );
    dispatch(setShowPreloader(false));
    if (res?.status === 200) {
      setFieldValue("customer_id", res.data.data.user_id);
      setValidCus(true);
      setCustomerInfo(res.data.data);
      SuccessToast("Customer found");
    } else {
      if (res?.status === 401) HandleLogout();
      setValidCus(false);
      setCustomerInfo(null);
      ErrorToast("Customer not found");
    }
  };

  return (
    <div className="min-h-screen bg-[linear-gradient(180deg,#edf5f8_0%,#f8fbfd_42%,#edf4f8_100%)] px-4 py-6 sm:px-6">
      <div className="mx-auto max-w-4xl space-y-5">

        {/* ── Header ─────────────────────────────────────────────────────── */}
        <div className="flex items-center gap-4 rounded-3xl bg-[linear-gradient(135deg,#0f172a_0%,#132238_50%,#0f1d30_100%)] px-6 py-5 shadow-xl ring-1 ring-white/10">
          <div className="flex h-12 w-12 shrink-0 items-center justify-center rounded-2xl border border-teal-500/30 bg-teal-500/10 text-2xl">
            📁
          </div>
          <div>
            <h1 className="text-lg font-black tracking-wide text-white">
              Upload Old Record
            </h1>
            <p className="mt-0.5 text-xs text-teal-300/60">
              Add a past event into the system
            </p>
          </div>

          {/* Past event badge */}
          <div className="ml-auto flex items-center gap-2 rounded-xl border border-teal-500/30 bg-teal-500/5 px-3 py-1.5">
            <span className="text-teal-400 text-xs">⏮</span>
            <span className="text-xs font-bold text-teal-400">
              Past Event
            </span>
          </div>
        </div>

        {/* ── Form Card ──────────────────────────────────────────────────── */}
        <div className="overflow-hidden rounded-3xl border border-white/80 bg-white/95 shadow-xl shadow-slate-200/70">
          <Formik
            innerRef={formikRef}
            enableReinitialize
            initialValues={initialValues}
            validationSchema={validationSchema}
            onSubmit={handleSubmit}
          >
            {({ setFieldValue, setFieldTouched, values, errors, touched }) => (
              <Form ref={formRef} onKeyDown={handleKeyDown}>
                <div className="p-6 space-y-6">

                  {/* ── Event Info ─────────────────────────────────────── */}
                  <div>
                    <SectionHeader
                      icon="📋"
                      title="Event Information"
                      sub="Type, past date and venue details"
                    />
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">

                      {/* Event Type */}
                      <FieldBox
                        label="Event Type"
                        required
                        error={touched.event_type && errors.event_type}
                      >
                        <div className="relative">
                          <Field
                            as="select"
                            name="event_type"
                            className={selectCls}
                            onChange={(e) => {
                              setFieldValue("event_type", e.target.value);
                              if (e.target.value !== "Custom")
                                setFieldValue("custom_event_type", "");
                            }}
                          >
                            <option value="">Select event type</option>
                            {eventTypes.map((type, i) => (
                              <option key={i} value={type}>
                                {type}
                              </option>
                            ))}
                            <option value="Custom">Custom</option>
                          </Field>
                          <span className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-500 pointer-events-none text-xs">
                            ▼
                          </span>
                        </div>

                        {values.event_type === "Custom" && (
                          <div className="mt-2">
                            <Field
                              type="text"
                              name="custom_event_type"
                              placeholder="Enter custom event type"
                              className={inputCls}
                            />
                            <ErrorMessage
                              name="custom_event_type"
                              component="p"
                              className="text-xs font-semibold text-red-400 mt-1"
                            />
                          </div>
                        )}
                      </FieldBox>

                      {/* Event Owner / Family Name */}
                      <FieldBox
                        label="Event Owner / Family Name"
                        required
                        error={touched.event_owner_name && errors.event_owner_name}
                      >
                        <Field
                          type="text"
                          name="event_owner_name"
                          placeholder="Name of the event owner or family"
                          className={inputCls}
                        />
                      </FieldBox>

                      <FieldBox
                        label="Eventor 1"
                        error={touched.eventor_1 && errors.eventor_1}
                      >
                        <Field
                          type="text"
                          name="eventor_1"
                          placeholder="Primary eventor name"
                          className={inputCls}
                        />
                      </FieldBox>

                      <FieldBox
                        label="Eventor 2"
                        error={touched.eventor_2 && errors.eventor_2}
                      >
                        <Field
                          type="text"
                          name="eventor_2"
                          placeholder="Secondary eventor name"
                          className={inputCls}
                        />
                      </FieldBox>

                      {/* Event Date — past dates only; Enter cycles DD→MM→YYYY→HH→MM */}
                      <FieldBox
                        label="Event Date & Time"
                        required
                        error={touched.event_date && errors.event_date}
                      >
                        <DateTimeSegmentInput
                          name="event_date"
                          formikValue={values.event_date}
                          setFieldValue={setFieldValue}
                          setFieldTouched={setFieldTouched}
                          accent="teal"
                        />
                      </FieldBox>

                      {/* Address */}
                      <FieldBox
                        label="Event Address"
                        required
                        span={2}
                        error={touched.event_address && errors.event_address}
                      >
                        <Field
                          as="textarea"
                          name="event_address"
                          rows={2}
                          placeholder="Enter full venue address"
                          className={textareaCls}
                        />
                      </FieldBox>

                      {/* City */}
                      <FieldBox
                        label="City"
                        required
                        error={touched.event_city && errors.event_city}
                      >
                        <CitySearchSelect
                          value={values.event_city}
                          onChange={(city) => setFieldValue("event_city", city)}
                          onBlur={() => setFieldTouched("event_city", true)}
                          className={inputCls}
                          placeholder="Search Tamil Nadu city…"
                        />
                      </FieldBox>

                      {/* State */}
                      <FieldBox
                        label="State"
                        required
                        error={touched.event_state && errors.event_state}
                      >
                        <Field
                          type="text"
                          name="event_state"
                          placeholder="State"
                          className={inputCls}
                        />
                      </FieldBox>

                      {/* Pincode */}
                      <FieldBox
                        label="Pincode"
                        required
                        error={touched.event_pincode && errors.event_pincode}
                      >
                        <Field
                          type="number"
                          name="event_pincode"
                          placeholder="6-digit pincode"
                          className={inputCls}
                        />
                      </FieldBox>
                    </div>
                  </div>

                  {/* ── Customer & Invitation ──────────────────────────── */}
                  <div>
                    <SectionHeader
                      icon="👤"
                      title="Customer & Invitation"
                      sub="Link a customer and optionally attach an invite"
                    />
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">

                      {/* Customer Mobile */}
                      <FieldBox
                        label="Customer Mobile"
                        required
                        error={
                          touched.customer_mobile && errors.customer_mobile
                        }
                      >
                        <Field
                          type="text"
                          name="customer_mobile"
                          maxLength={10}
                          placeholder="10-digit number"
                          className={inputCls}
                          onInput={(e) => {
                            if (e.target.value.length >= 10) {
                              checkCustomerMobile(
                                e.target.value,
                                setFieldValue
                              );
                            }
                          }}
                        />
                        <CustomerBadge validCus={validCus} />
                      </FieldBox>

                      {/* Invitation */}
                      <FieldBox
                        label="Event Invitation"
                        error={touched.invitation && errors.invitation}
                      >
                        <div className="rounded-xl border border-dashed border-amber-200 bg-amber-50/60 p-3">
                          <DesktopFileUpload
                            innerText="Upload Invitation Photo"
                            accept="image/*"
                            selectedFileURL={(file) =>
                              setFieldValue("invitation", file)
                            }
                          />
                          {values.invitation && (
                            <p className="text-emerald-400 flex items-center mt-2 gap-1.5 text-xs font-semibold">
                              <FaCheckCircle />
                              Invitation uploaded
                            </p>
                          )}
                        </div>
                      </FieldBox>

                      <CustomerInfoCard info={customerInfo} />
                    </div>
                  </div>
                </div>

                {/* ── Submit ────────────────────────────────────────────── */}
                <div className="px-6 pb-6">
                  <button
                    type="submit"
                    className="group flex w-full items-center justify-center gap-2 rounded-xl bg-[linear-gradient(135deg,#0f766e_0%,#155e75_100%)] py-3.5 text-sm font-bold text-white shadow-lg shadow-teal-700/20 transition duration-200 hover:opacity-95 focus:outline-none focus:ring-2 focus:ring-teal-500/30"
                  >
                    <span>Upload Old Record</span>
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      className="h-4 w-4 group-hover:translate-x-1 transition-transform"
                      fill="none"
                      viewBox="0 0 24 24"
                      stroke="currentColor"
                      strokeWidth={2.5}
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        d="M13 7l5 5m0 0l-5 5m5-5H6"
                      />
                    </svg>
                  </button>
                </div>
              </Form>
            )}
          </Formik>
        </div>
      </div>

      {/* ── Modal ──────────────────────────────────────────────────────────── */}
      <CustomModal
        type="error"
        showModal={showCusWrngModal}
        bodycontent={
          <>
            <p className="font-medium my-3 text-sm">
              The entered customer number is not registered. Please register
              using this{" "}
              <button
                type="button"
                onClick={() => {
                  sessionStorage.setItem(
                    OLD_RECORD_DRAFT_STORAGE_KEY,
                    JSON.stringify({
                      ...pendingRegistrationDraft,
                      customer_id: "",
                      invitation: "",
                    }),
                  );
                  const registerParams = new URLSearchParams({
                    fromEventCreation: "true",
                    returnTo: "old-record-upload",
                  });
                  if (pendingCustomerMobile) {
                    registerParams.set("mobile", pendingCustomerMobile);
                  }
                  navigate(`/dashboard/add-customer?${registerParams.toString()}`);
                  setShowCusWrngModal(false);
                }}
                className="text-teal-700 underline font-bold"
              >
                link
              </button>{" "}
              to continue.
            </p>
            <div className="text-right">
              <button
                className="bg-gray-800 hover:bg-gray-700 text-white px-4 py-1.5 rounded-lg text-sm font-semibold transition"
                onClick={() => setShowCusWrngModal(false)}
              >
                Close
              </button>
            </div>
          </>
        }
      />
    </div>
  );
};

export default UploadOldRecord;
